// Imports
import unified from 'unified';
import remarkParse from 'remark-parse';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import remarkRehype from 'remark-rehype';
import rehypeRaw from 'rehype-raw';
import rehypeStringify from 'rehype-stringify';
import visit from 'unist-util-visit';
import type { Root, Element } from 'hast';
import type { Parent } from 'unist';


/** Parse a string as Markdown into HTML, this HTML is not sanitized */
export default function parseMarkdown(markdown: string): string {
	// Transfer the value
	let content = markdown;
	
	
	// Prevent HTML from being written
	//  -> Replaces all < characters to &lt; including escaped characters
	//  -> The first replacement replaces escaped < characters, which the Markdown editor should always do
	//  -> The second replacement replaces unescaped < characters, which should only be possible if a user tries to interact with the endpoint directly (likely with bad intent)
	content = content.replace(/\\</g, '&lt;');
	content = content.replace(/</g, '&lt;');
	
	
	// Encode quotes
	content = content.replace(/'/g, '&#39;');
	content = content.replace(/"/g, '&quot;');
	
	
	// Inject buttons
	content = content.replace(
		/\[\\\[\\\[(.+?)\\\]\\\]\]\((.+?)\)/g,
		'<a href="$2" class="btn-primary" style="margin: 10px 0;">$1</a>'
	);
	
	
	// Wrap tables in a div for styling in `<EditorGeneratedContentContainer />`
	//  -> Written inline because `unified` version 9's incomplete cross-domain TypeScript generics make a typed `Plugin` annotation awkward here
	const rehypeWrapTables = () => (tree: Root) => {
		visit(tree, 'element', (node: Element, index: number | undefined, parent: Parent | undefined) => {
			if (node.tagName !== 'table' || !parent || index === undefined) {
				return;
			}
			
			(parent.children as Element[])[index] = {
				type: 'element',
				tagName: 'div',
				properties: { className: ['table'] },
				children: [node],
			};
		});
	};
	
	
	// Parse Markdown
	//  -> `allowDangerousHtml` + `rehype-raw` allows injected button HTML to pass through
	//  -> Cast to `any` because `unified` version 9's types don't model the `remark` → `rehype` tree-type transition
	// LATER: Revisit when newer version of `unified` provides better type definitions
	// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
	const result = (unified() as any)
		.use(remarkParse)
		.use(remarkGfm)
		.use(remarkBreaks)
		.use(remarkRehype, { allowDangerousHtml: true })
		.use(rehypeRaw)
		.use(rehypeWrapTables)
		.use(rehypeStringify)
		.processSync(content);
	
	
	// Return
	return String(result).trim();
}
